import {
  BankOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  FileTextOutlined,
  HomeOutlined,
  MailOutlined,
  PhoneOutlined,
  PlusOutlined,
  TagsOutlined,
  TeamOutlined,
  UserAddOutlined,
  UserDeleteOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Form,
  Modal,
  message,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  Timeline,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../config/api';

const { TabPane } = Tabs;
const { Option } = Select;
const { Title, Text } = Typography;

interface CompanyDetailsProps {
  companyId: string;
}

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  role: string;
}

interface Ticket {
  id: string;
  ticketNumber: string;
  email: string;
  firstName: string;
  lastName: string;
  personalEmail?: string;
  status: string;
  checkedIn: boolean;
  checkedInAt?: string;
  userId?: string;
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  event: {
    id: string;
    title: string;
    startDate: string;
  };
  ticketType: {
    name: string;
    price: number;
  };
}

interface Company {
  id: string;
  name: string;
  taxNumber?: string;
  address?: string;
  contactEmail: string;
  contactPhone?: string;
  contactPerson?: string;
  billingAddress?: string;
  notes?: string;
  organization: {
    id: string;
    name: string;
  };
  users: User[];
  tickets: Ticket[];
  createdAt: string;
  updatedAt: string;
}

const CompanyDetails: React.FC<CompanyDetailsProps> = ({ companyId }) => {
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [isAddUserModalVisible, setIsAddUserModalVisible] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    fetchCompanyDetails();
  }, [companyId]);

  const fetchCompanyDetails = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/companies/${companyId}`);
      setCompany(response.data);
    } catch (error) {
      message.error(t('companyDetails.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableUsers = async () => {
    try {
      const response = await api.get('/users');
      const usersWithoutCompany = response.data.filter((user: any) => !user.companyId);
      setAvailableUsers(usersWithoutCompany);
    } catch (error) {
      message.error(t('companyDetails.fetchUsersFailed'));
    }
  };

  const handleAddUser = async () => {
    if (!selectedUserId) {
      message.warning(t('companyDetails.selectUserWarning'));
      return;
    }

    try {
      await api.post(`/companies/${companyId}/users`, { userId: selectedUserId });
      message.success(t('companyDetails.userAdded'));
      setIsAddUserModalVisible(false);
      setSelectedUserId(null);
      fetchCompanyDetails();
    } catch (error) {
      message.error(t('companyDetails.addUserFailed'));
    }
  };

  const handleRemoveUser = async (userId: string) => {
    try {
      await api.delete(`/companies/${companyId}/users/${userId}`);
      message.success(t('companyDetails.userRemoved'));
      fetchCompanyDetails();
    } catch (error) {
      message.error(t('companyDetails.removeUserFailed'));
    }
  };

  const handleCreateTickets = () => {
    Modal.info({
      title: t('companyDetails.bulkTicketsTitle'),
      content: t('companyDetails.bulkTicketsContent'),
      onOk() {},
    });
  };

  const userColumns: ColumnsType<User> = [
    {
      title: t('companyDetails.columnName'),
      key: 'name',
      render: (_, record) => (
        <Space>
          <TeamOutlined />
          <strong>
            {record.firstName} {record.lastName}
          </strong>
        </Space>
      ),
    },
    {
      title: t('companyDetails.columnEmail'),
      dataIndex: 'email',
      key: 'email',
      render: (text) => (
        <Space>
          <MailOutlined />
          {text}
        </Space>
      ),
    },
    {
      title: t('companyDetails.columnPhone'),
      dataIndex: 'phone',
      key: 'phone',
      render: (text) => text || <Tag>{t('companyDetails.noPhone')}</Tag>,
    },
    {
      title: t('companyDetails.columnRole'),
      dataIndex: 'role',
      key: 'role',
      render: (role) => (
        <Tag color={role === 'ADMIN' ? 'red' : role === 'EVENT_ADMIN' ? 'orange' : 'blue'}>
          {role}
        </Tag>
      ),
    },
    {
      title: t('companyDetails.columnActions'),
      key: 'actions',
      render: (_, record) => (
        <Popconfirm
          title={t('companyDetails.removeConfirmTitle')}
          description={t('companyDetails.removeConfirmDesc')}
          onConfirm={() => handleRemoveUser(record.id)}
          okText={t('companyDetails.yes')}
          cancelText={t('companyDetails.no')}
        >
          <Button danger icon={<UserDeleteOutlined />} size="small">
            {t('companyDetails.remove')}
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const ticketColumns: ColumnsType<Ticket> = [
    {
      title: t('companyDetails.columnTicketNumber'),
      dataIndex: 'ticketNumber',
      key: 'ticketNumber',
      render: (text) => <Text copyable>{text}</Text>,
      width: 180,
    },
    {
      title: t('companyDetails.columnAssignedTo'),
      key: 'assignedTo',
      render: (_, record) => (
        <Space direction="vertical" size="small">
          {record.userId && record.user ? (
            <>
              <Space>
                <UserOutlined />
                <strong>
                  {record.user?.firstName} {record.user?.lastName}
                </strong>
              </Space>
              <Text type="secondary">{record.user?.email}</Text>
            </>
          ) : (
            <Tag color="orange" icon={<ClockCircleOutlined />}>
              {t('companyDetails.awaitingAssignment')}
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: t('companyDetails.columnAttendee'),
      key: 'attendee',
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <strong>
            {record.firstName} {record.lastName}
          </strong>
          <Space>
            <MailOutlined />
            {record.personalEmail || record.email}
          </Space>
        </Space>
      ),
    },
    {
      title: t('companyDetails.columnEvent'),
      dataIndex: ['event', 'title'],
      key: 'event',
      render: (text, record) => (
        <Space direction="vertical" size="small">
          <strong>{text || t('companyDetails.na')}</strong>
          <Text type="secondary">
            <CalendarOutlined />{' '}
            {record.event?.startDate
              ? dayjs(record.event.startDate).format('MMM DD, YYYY')
              : t('companyDetails.na')}
          </Text>
        </Space>
      ),
    },
    {
      title: t('companyDetails.columnTicketType'),
      key: 'ticketType',
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <Tag color="blue">{record.ticketType?.name || t('companyDetails.na')}</Tag>
          <Text>
            <DollarOutlined /> ${record.ticketType?.price || 0}
          </Text>
        </Space>
      ),
    },
    {
      title: t('companyDetails.columnStatus'),
      key: 'status',
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <Badge
            status={
              record.status === 'PAID'
                ? 'success'
                : record.status === 'PENDING'
                  ? 'warning'
                  : 'default'
            }
            text={record.status}
          />
          {record.checkedIn ? (
            <Tag icon={<CheckCircleOutlined />} color="success">
              {t('companyDetails.checkedIn')}
            </Tag>
          ) : (
            <Tag icon={<ClockCircleOutlined />} color="default">
              {t('companyDetails.notCheckedIn')}
            </Tag>
          )}
        </Space>
      ),
    },
  ];

  if (loading || !company) {
    return <div>{t('companyDetails.loading')}</div>;
  }

  const totalTicketValue =
    company.tickets?.reduce((sum, ticket) => sum + (ticket?.ticketType?.price || 0), 0) || 0;
  const checkedInCount = company.tickets?.filter((t) => t.checkedIn).length || 0;

  return (
    <div>
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane
          tab={
            <span>
              <BankOutlined />
              {t('companyDetails.tabOverview')}
            </span>
          }
          key="overview"
        >
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={6}>
              <Card>
                <Statistic
                  title={t('companyDetails.statTotalUsers')}
                  value={company.users?.length || 0}
                  prefix={<TeamOutlined />}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title={t('companyDetails.statTotalTickets')}
                  value={company.tickets?.length || 0}
                  prefix={<TagsOutlined />}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title={t('companyDetails.statCheckedIn')}
                  value={checkedInCount}
                  suffix={`/ ${company.tickets?.length || 0}`}
                  prefix={<CheckCircleOutlined />}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title={t('companyDetails.statTotalValue')}
                  value={totalTicketValue}
                  prefix="$"
                  precision={2}
                />
              </Card>
            </Col>
          </Row>

          <Card title={t('companyDetails.cardCompanyInfo')}>
            <Descriptions bordered column={2}>
              <Descriptions.Item label={t('companyDetails.labelCompanyName')} span={1}>
                <Space>
                  <BankOutlined />
                  <strong>{company.name}</strong>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label={t('companyDetails.labelTaxNumber')} span={1}>
                {company.taxNumber || <Tag>{t('companyDetails.notProvided')}</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label={t('companyDetails.labelOrganization')} span={2}>
                <Tag color="purple">{company.organization?.name || t('companyDetails.na')}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('companyDetails.labelContactPerson')} span={1}>
                <Space>
                  <UserOutlined />
                  {company.contactPerson || t('companyDetails.notSpecified')}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label={t('companyDetails.labelContactEmail')} span={1}>
                <Space>
                  <MailOutlined />
                  <a href={`mailto:${company.contactEmail}`}>{company.contactEmail}</a>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label={t('companyDetails.labelContactPhone')} span={2}>
                <Space>
                  <PhoneOutlined />
                  {company.contactPhone || t('companyDetails.notProvided')}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label={t('companyDetails.labelCompanyAddress')} span={2}>
                <Space>
                  <HomeOutlined />
                  {company.address || t('companyDetails.notProvided')}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label={t('companyDetails.labelBillingAddress')} span={2}>
                <Space>
                  <FileTextOutlined />
                  {company.billingAddress || t('companyDetails.sameAsCompanyAddress')}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label={t('companyDetails.labelInternalNotes')} span={2}>
                {company.notes ? (
                  <Alert message={company.notes} type="info" />
                ) : (
                  <Text type="secondary">{t('companyDetails.noNotes')}</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t('companyDetails.labelCreated')} span={1}>
                {dayjs(company.createdAt).format('MMM DD, YYYY HH:mm')}
              </Descriptions.Item>
              <Descriptions.Item label={t('companyDetails.labelLastUpdated')} span={1}>
                {dayjs(company.updatedAt).format('MMM DD, YYYY HH:mm')}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </TabPane>

        <TabPane
          tab={
            <span>
              <TeamOutlined />
              {t('companyDetails.tabUsers', { count: company.users?.length || 0 })}
            </span>
          }
          key="users"
        >
          <Card
            title={t('companyDetails.cardCompanyUsers')}
            extra={
              <Button
                type="primary"
                icon={<UserAddOutlined />}
                onClick={() => {
                  fetchAvailableUsers();
                  setIsAddUserModalVisible(true);
                }}
              >
                {t('companyDetails.addUser')}
              </Button>
            }
          >
            {company.users && company.users.length > 0 ? (
              <Table
                columns={userColumns}
                dataSource={company.users}
                rowKey="id"
                pagination={false}
              />
            ) : (
              <Empty description={t('companyDetails.noUsers')} image={Empty.PRESENTED_IMAGE_SIMPLE}>
                <Button
                  type="primary"
                  icon={<UserAddOutlined />}
                  onClick={() => {
                    fetchAvailableUsers();
                    setIsAddUserModalVisible(true);
                  }}
                >
                  {t('companyDetails.addFirstUser')}
                </Button>
              </Empty>
            )}
          </Card>
        </TabPane>

        <TabPane
          tab={
            <span>
              <TagsOutlined />
              {t('companyDetails.tabTickets', { count: company.tickets?.length || 0 })}
            </span>
          }
          key="tickets"
        >
          <Card
            title={t('companyDetails.cardCompanyTickets')}
            extra={
              <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateTickets}>
                {t('companyDetails.createBulkTickets')}
              </Button>
            }
          >
            {company.tickets && company.tickets.length > 0 ? (
              <>
                {/* Statistics Row */}
                <Row gutter={16} style={{ marginBottom: 24 }}>
                  <Col span={6}>
                    <Card>
                      <Statistic
                        title={t('companyDetails.statAllTickets')}
                        value={company.tickets?.length || 0}
                        prefix={<TagsOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col span={6}>
                    <Card>
                      <Statistic
                        title={t('companyDetails.statAssigned')}
                        value={company.tickets?.filter((t) => t.userId).length || 0}
                        prefix={<UserOutlined />}
                        valueStyle={{ color: '#52c41a' }}
                      />
                    </Card>
                  </Col>
                  <Col span={6}>
                    <Card>
                      <Statistic
                        title={t('companyDetails.awaitingAssignment')}
                        value={company.tickets?.filter((t) => !t.userId).length || 0}
                        prefix={<ClockCircleOutlined />}
                        valueStyle={{ color: '#fa8c16' }}
                      />
                    </Card>
                  </Col>
                  <Col span={6}>
                    <Card>
                      <Statistic
                        title={t('companyDetails.statCheckedInHu')}
                        value={checkedInCount}
                        prefix={<CheckCircleOutlined />}
                        valueStyle={{ color: '#1890ff' }}
                      />
                    </Card>
                  </Col>
                </Row>

                {/* Tabs for assigned and unassigned tickets */}
                <Tabs defaultActiveKey="all">
                  <TabPane
                    tab={t('companyDetails.innerTabAll', { count: company.tickets?.length || 0 })}
                    key="all"
                  >
                    <Table
                      columns={ticketColumns}
                      dataSource={company.tickets}
                      rowKey="id"
                      pagination={{
                        defaultPageSize: 10,
                        showSizeChanger: true,
                      }}
                    />
                  </TabPane>
                  <TabPane
                    tab={
                      <Badge
                        count={company.tickets?.filter((t) => t.userId).length || 0}
                        style={{ backgroundColor: '#52c41a' }}
                      >
                        <span style={{ paddingRight: 8 }}>{t('companyDetails.statAssigned')}</span>
                      </Badge>
                    }
                    key="assigned"
                  >
                    <Table
                      columns={ticketColumns}
                      dataSource={company.tickets?.filter((t) => t.userId) || []}
                      rowKey="id"
                      pagination={{
                        defaultPageSize: 10,
                        showSizeChanger: true,
                      }}
                    />
                  </TabPane>
                  <TabPane
                    tab={
                      <Badge
                        count={company.tickets?.filter((t) => !t.userId).length || 0}
                        style={{ backgroundColor: '#fa8c16' }}
                      >
                        <span style={{ paddingRight: 8 }}>
                          {t('companyDetails.innerTabUnassigned')}
                        </span>
                      </Badge>
                    }
                    key="unassigned"
                  >
                    <Alert
                      message={t('companyDetails.innerTabUnassigned')}
                      description={t('companyDetails.unassignedAlertDesc')}
                      type="warning"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />
                    <Table
                      columns={ticketColumns}
                      dataSource={company.tickets?.filter((t) => !t.userId) || []}
                      rowKey="id"
                      pagination={{
                        defaultPageSize: 10,
                        showSizeChanger: true,
                      }}
                    />
                  </TabPane>
                </Tabs>
              </>
            ) : (
              <Empty
                description={t('companyDetails.noTickets')}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              >
                <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateTickets}>
                  {t('companyDetails.createFirstTickets')}
                </Button>
              </Empty>
            )}
          </Card>
        </TabPane>

        <TabPane
          tab={
            <span>
              <CalendarOutlined />
              {t('companyDetails.tabTimeline')}
            </span>
          }
          key="timeline"
        >
          <Card title={t('companyDetails.cardRecentActivity')}>
            <Timeline mode="left">
              <Timeline.Item color="green" label={dayjs(company.createdAt).format('MMM DD, YYYY')}>
                {t('companyDetails.companyCreated')}
              </Timeline.Item>
              {company.users?.map((user) => (
                <Timeline.Item key={user.id} color="blue" dot={<TeamOutlined />}>
                  {t('companyDetails.userAddedTimeline', {
                    name: `${user.firstName} ${user.lastName}`,
                  })}
                </Timeline.Item>
              ))}
              {company.tickets?.slice(0, 5).map((ticket) => (
                <Timeline.Item
                  key={ticket.id}
                  color={ticket.checkedIn ? 'green' : 'gray'}
                  dot={<TagsOutlined />}
                >
                  {t('companyDetails.ticketTimeline', {
                    number: ticket.ticketNumber,
                    event: ticket.event?.title || t('companyDetails.na'),
                  })}
                  {ticket.checkedIn && t('companyDetails.checkedInSuffix')}
                </Timeline.Item>
              ))}
              {company.tickets && company.tickets.length > 5 && (
                <Timeline.Item>
                  {t('companyDetails.andMoreTickets', { count: company.tickets.length - 5 })}
                </Timeline.Item>
              )}
            </Timeline>
          </Card>
        </TabPane>
      </Tabs>

      {/* Add User Modal */}
      <Modal
        title={t('companyDetails.addUserModalTitle')}
        open={isAddUserModalVisible}
        onOk={handleAddUser}
        onCancel={() => {
          setIsAddUserModalVisible(false);
          setSelectedUserId(null);
        }}
      >
        <Form layout="vertical">
          <Form.Item label={t('companyDetails.labelSelectUser')}>
            <Select
              placeholder={t('companyDetails.selectUserPlaceholder')}
              value={selectedUserId}
              onChange={setSelectedUserId}
              showSearch
              filterOption={(input, option) =>
                (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
              }
            >
              {availableUsers.map((user) => (
                <Option key={user.id} value={user.id}>
                  {user.firstName} {user.lastName} - {user.email}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CompanyDetails;
